// A URL identity for a local candidate.
//
// This module was built on the assumption that these rows had no identifier and
// one had to be composed from county, office and name. A live response says
// otherwise: every row carries a real `id`, so the composition is gone and the
// ref is the id.
//
// The county still travels with it, in front:
//
//     /us/candidate/local/IN/county/wayne-county--495872
//
// Not as identity -- the id alone is unique -- but as a narrowing hint for the
// lookup. /elections/candidates has no by-id parameter, so the page finds its
// row by fetching a list and searching it, and Indiana files 8,670 non-federal
// candidates. Without a county the row may not be in the page that comes back.
//
// State-level contests carry no county in the ref. Iowa publishes its statewide
// races inside each county's PDF, so those rows do have a county_name, but
// level=state with county= is a 422 -- a statewide contest is not a county
// contest however it was filed.

const EMPTY = '_';
const SEPARATOR = '--';

// Runs of anything that is not a letter or digit collapse to a single hyphen,
// so a part can contain '-' but never '--' and the separator stays unambiguous.
export const slugPart = (value) => {
  const slug = String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug || EMPTY;
};

// Returns null for a row with no id rather than composing a fallback identity.
// The caller renders such a row as non-clickable, which is honest: a link that
// cannot resolve is worse than no link.
export const encodeLocalCandidateRef = (candidate) => {
  if (!candidate?.id) return null;

  const narrows = candidate.level !== 'state';
  const place = narrows ? candidate.county || candidate.jurisdiction : '';

  return [slugPart(place), String(candidate.id)].join(SEPARATOR);
};

export const decodeLocalCandidateRef = (ref) => {
  const parts = String(ref || '').split(SEPARATOR);
  if (parts.length < 2) return null;

  const id = parts[parts.length - 1];
  // Ids are integers. Anything else is a ref from the composed scheme this
  // replaced, or a hand-edited URL, and neither can be resolved.
  if (!/^\d+$/.test(id)) return null;

  const county = parts.slice(0, parts.length - 1).join(SEPARATOR);

  return {
    countySlug: county || EMPTY,
    id,
  };
};

// The county the ref was built from, back in a shape api2 will filter on.
// api2 takes a county with or without the noun, and the hyphens go back to
// spaces; the match is not case sensitive at either end.
export const countyQueryFromRef = (parts) =>
  parts && parts.countySlug !== EMPTY ? parts.countySlug.replace(/-/g, ' ') : '';

export const candidateMatchesRef = (candidate, parts) =>
  Boolean(candidate && parts && String(candidate.id) === parts.id);

export { EMPTY as EMPTY_REF_PART, SEPARATOR as REF_SEPARATOR };